/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useForm, Control, UseFormReturn } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'

import { ENDPOINTS } from '@/util'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { useSubmitForm } from '../../FormComponent/useSubmitForm'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'

import { DataFormResponse } from '../../DataFormComponent/types'
import { populateDefaultFormValues } from '../../DataFormComponent/utils'
import { JsonFormPropertyV2, FormValues } from '../../FormComponent/types'
import { DataTableV2InlineEditConfig, DataTableV2Column, DataTableV2DataRow } from '../types'
import { isTitleValueObject, transformValuesToTitleValueObjects } from '../utils'
import { useGetPageFilters } from '@/catalog-apps/components/AppPage2/Filter/useGetPageFilters'
import { TComponentType } from '@/catalog-apps/components/AppPage2/types'

// ─── Types ───────────────────────────────────────────────────────────────────

/** Separator used to encode rowId + fieldId into a flat form field name */
const FIELD_SEPARATOR = '__'

/** Builds a form field name from row id and field id */
function buildFieldName(rowId: string | number, fieldId: string): string {
  return `${String(rowId)}${FIELD_SEPARATOR}${fieldId}`
}

/** Parses a form field name back into { rowId, fieldId } */
function parseFieldName(fieldName: string): { rowId: string; fieldId: string } | null {
  const idx = fieldName.indexOf(FIELD_SEPARATOR)
  if (idx === -1) return null
  return {
    rowId: fieldName.substring(0, idx),
    fieldId: fieldName.substring(idx + FIELD_SEPARATOR.length),
  }
}

/** Returns a sensible empty default value for a given form field type to avoid uncontrolled→controlled warnings */
function getDefaultForFieldType(type: string): unknown {
  switch (type) {
    case 'string':
      return ''
    case 'number':
    case 'integer':
      return ''
    case 'boolean':
      return false
    case 'array':
      return []
    default:
      return ''
  }
}

// ─── Fetch helper ────────────────────────────────────────────────────────────

interface FetchDataFormParams {
  appKey?: string
  contentId?: string
  projectId?: number
  filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
}

const fetchDataForm = async ({
  appKey,
  contentId,
  projectId,
  filterSelections,
  isPageBuilder,
}: FetchDataFormParams): Promise<DataFormResponse> => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder) {
    return populateDefaultFormValues({
      ...(await import('@/catalog-apps/mock-data/data-form/data-form-response-grid-1-row.json')).json,
    } as DataFormResponse)
  }
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) throw new Error('No token found')

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: filterSelections,
    }),
  })
  const data = (await response.json()) as DataFormResponse
  if (response.ok) return populateDefaultFormValues(data)
  throw new Error('Failed to fetch inline edit form schema')
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UseInlineEditV2Params {
  inlineEditConfig?: DataTableV2InlineEditConfig
  localData: DataTableV2DataRow[]
  columns: DataTableV2Column[]
}

export interface UseInlineEditV2Return {
  /** Whether inline editing mode is currently active */
  isInlineEditing: boolean
  /** Whether the form has been initialized with proper default values and is ready to render fields */
  isFormReady: boolean
  /** Whether the form schema is currently loading */
  isLoadingFormSchema: boolean
  /** Map of column id → JsonFormPropertyV2 for columns that have a matching form property */
  formPropertyMap: Map<string, JsonFormPropertyV2>
  /** React-hook-form control for the inline edit form */
  control: Control<FormValues>
  /** Full react-hook-form methods (needed for FormProvider) */
  formMethods: UseFormReturn<FormValues>
  /** Toggle inline editing mode on/off */
  onToggleInlineEdit: () => void
  /** Save edited rows */
  onSaveInlineEdit: () => void
  /** Cancel inline editing and reset form */
  onCancelInlineEdit: () => void
  /** Whether the save mutation is pending */
  isSubmitting: boolean
  /** Get the form field name for a specific row + column combination */
  getFieldName: (rowId: string | number, fieldId: string) => string
}

export function useInlineEditV2({
  inlineEditConfig,
  localData,
  columns,
}: UseInlineEditV2Params): UseInlineEditV2Return {
  const [isInlineEditing, setIsInlineEditing] = useState(false)
  const [isFormReady, setIsFormReady] = useState(false)

  // ─── Context hooks ──────────────────────────────────────────────────────────
  const isPageBuilder = useIsPageBuilder()
  const {
    data: { defaultSelections },
  } = useGetPageFilters()
  const { filterSelections } = useGetFilterSelections()
  const { appKey, projectId } = useAppContext()
  const { tileFilterSelections } = useTileFilters()

  const filterSelectionsArray = useMemo(
    () =>
      filterSelections.length > 0
        ? [...filterSelections, ...tileFilterSelections]
        : [...defaultSelections, ...tileFilterSelections],
    [filterSelections, tileFilterSelections, defaultSelections]
  )

  // ─── Fetch form schema (non-suspense, only when editing) ────────────────────
  const contentId = inlineEditConfig?.formSchemaContentId || ''
  const componentType: TComponentType = 'data-form'

  const {
    data: formSchemaData,
    isLoading: isLoadingFormSchema,
    isFetched: isFormSchemaFetched,
  } = useQuery({
    queryKey: [contentId, componentType, 'inline-edit', appKey, projectId, filterSelectionsArray, isPageBuilder],
    queryFn: () =>
      fetchDataForm({
        appKey,
        contentId,
        projectId,
        filterSelections: filterSelectionsArray,
        isPageBuilder,
      }),
    enabled: isInlineEditing && !!contentId,
  })

  // ─── Map form properties to column IDs ──────────────────────────────────────
  const formPropertyMap = useMemo(() => {
    if (!formSchemaData) return new Map<string, JsonFormPropertyV2>()
    const properties = formSchemaData.uiSchema.schema.properties
    const columnIdSet = new Set(columns.map((c) => c.id))
    const map = new Map<string, JsonFormPropertyV2>()
    for (const prop of properties) {
      if (columnIdSet.has(prop.id)) {
        map.set(prop.id, prop)
      }
    }
    return map
  }, [formSchemaData, columns])

  // ─── Compute default values for the form ────────────────────────────────────
  const defaultValues = useMemo(() => {
    if (!isInlineEditing || !isFormSchemaFetched || formPropertyMap.size === 0) return {}
    const values: Record<string, unknown> = {}

    /** Extracts form default values for a single data row */
    const processRow = (row: DataTableV2DataRow) => {
      formPropertyMap.forEach((prop, colId) => {
        const cellValue = row[colId]
        const fieldName = buildFieldName(row.id, colId)
        // Extract primitive value from title-value objects
        if (isTitleValueObject(cellValue)) {
          // Single title-value object → extract the primitive value
          values[fieldName] = (cellValue as { title: string; value: string | number }).value
        } else if (
          Array.isArray(cellValue) &&
          cellValue.length > 0 &&
          isTitleValueObject(cellValue[0])
        ) {
          // Array of title-value objects → extract an array of primitive values
          values[fieldName] = (cellValue as { title: string; value: string | number }[]).map(
            (item) => item.value
          )
        } else if (cellValue !== undefined && cellValue !== null) {
          values[fieldName] = cellValue
        } else {
          // Provide type-appropriate fallback to avoid uncontrolled → controlled warnings
          values[fieldName] = getDefaultForFieldType(prop.type)
        }
      })
    }

    for (const row of localData) {
      if (row.isGroup) {
        // Group rows are not editable, but their subRows are
        const subRows = (row as DataTableV2DataRow & { subRows?: DataTableV2DataRow[] }).subRows
        if (subRows) {
          for (const subRow of subRows) {
            processRow(subRow)
          }
        }
        continue
      }
      processRow(row)
    }
    return values
  }, [isInlineEditing, isFormSchemaFetched, formPropertyMap, localData])

  // ─── React-hook-form ────────────────────────────────────────────────────────
  const form = useForm<FormValues>({
    defaultValues: defaultValues as FormValues,
  })

  // Reset the form whenever default values are recomputed (e.g., form schema loaded).
  // Also marks the form as ready so inline edit columns can safely mount with proper values.
  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues as FormValues)
      setIsFormReady(true)
    }
  }, [defaultValues]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form-ready state when exiting inline editing
  useEffect(() => {
    if (!isInlineEditing) {
      setIsFormReady(false)
    }
  }, [isInlineEditing])

  // ─── Submit handler ─────────────────────────────────────────────────────────
  const { mutate: submitForm, isPending: isSubmitting } = useSubmitForm(
    inlineEditConfig?.handlerId || '',
    tileFilterSelections,
    undefined,
    {
      onSuccess: () => {
        setIsInlineEditing(false)
      },
    }
  )

  // ─── Actions ────────────────────────────────────────────────────────────────
  const onToggleInlineEdit = useCallback(() => {
    setIsInlineEditing((prev) => !prev)
  }, [])

  const onSaveInlineEdit = useCallback(() => {
    const allValues = form.getValues()
    const dirtyFields = form.formState.dirtyFields

    // Find which rows have dirty fields
    const dirtyRowIds = new Set<string>()
    for (const fieldName of Object.keys(dirtyFields)) {
      const parsed = parseFieldName(fieldName)
      if (parsed) {
        dirtyRowIds.add(parsed.rowId)
      }
    }

    if (dirtyRowIds.size === 0) {
      // Nothing changed, just exit edit mode
      setIsInlineEditing(false)
      return
    }

    // Reconstruct edited rows from form values
    const formProperties = formSchemaData?.uiSchema.schema.properties || []
    const editedRows: Record<string, unknown>[] = []

    for (const rowId of dirtyRowIds) {
      const row: Record<string, unknown> = { id: isNaN(Number(rowId)) ? rowId : Number(rowId) }
      formPropertyMap.forEach((_prop, fieldId) => {
        const fieldName = buildFieldName(rowId, fieldId)
        if (fieldName in allValues) {
          row[fieldId] = allValues[fieldName as keyof typeof allValues]
        }
      })
      // Transform values back to title-value objects where the form schema uses choices
      const transformedRow = transformValuesToTitleValueObjects(row, formProperties)
      editedRows.push(transformedRow)
    }

    submitForm({ rows: editedRows })
  }, [form, formPropertyMap, formSchemaData, submitForm])

  const onCancelInlineEdit = useCallback(() => {
    form.reset(defaultValues as FormValues)
    setIsInlineEditing(false)
  }, [form, defaultValues])

  const getFieldName = useCallback((rowId: string | number, fieldId: string) => buildFieldName(rowId, fieldId), [])

  return {
    isInlineEditing,
    isFormReady,
    isLoadingFormSchema,
    formPropertyMap,
    control: form.control,
    formMethods: form,
    onToggleInlineEdit,
    onSaveInlineEdit,
    onCancelInlineEdit,
    isSubmitting,
    getFieldName,
  }
}
